import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { HasPermissionDirective } from '../../../../../../directives/has-permission.directive';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { MeasureDetailService, MeasureKind } from '../../../../../../services/measure-detail.service';
import { ToastService } from '../../../../../../services/toast.service';
import { MenuItemWithIcon } from '../../../../../../types/menu.interface';
import {
  analysisPostDeletionDetailSentences,
  measureDeletionKeys,
  resolveMeasureDeletionBlock,
} from '../../../../../../utils/measure-deletion-block.util';
import { notifyMeasureDeletionError } from '../../../../../../utils/measure-deletion-error.util';
import { CesAssetBreadcrumbComponent } from '../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesMenuButtonSecondaryComponent } from '../../../../../ui/ces-buttons';
import { MeasureSkeletonComponent } from '../../../../../ui/skeletons/measure-skeleton.component';

@Component({
  selector: 'app-measure-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TabsModule,
    RouterModule,
    CesAssetBreadcrumbComponent,
    TagModule,
    MeasureSkeletonComponent,
    CesMenuButtonSecondaryComponent,
    HasPermissionDirective,
    TranslocoModule,
  ],
  providers: [MeasureDetailService],
  templateUrl: './measure-detail.component.html',
})
export class MeasureDetailComponent implements OnInit {
  protected readonly dataService = inject(MeasureDetailService);
  private readonly toastService = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationHelper = inject(ConfirmationHelperService);

  private readonly translations = toSignal(this.translate.selectTranslation());

  protected readonly tabs = ['info', 'formula', 'verification'] as const;
  protected activeTab: string = 'info';

  private readonly kind = this.route.snapshot.data['kind'] as MeasureKind;

  protected readonly measure = this.dataService.measure;

  protected readonly isUsed = computed<boolean>(() => Boolean(this.measure()?.hasValues));

  private readonly deletionBlock = computed(() =>
    resolveMeasureDeletionBlock(this.kind, this.isUsed(), this.dataService.referencingAnalysisPosts()),
  );

  protected readonly deleteMenuItems = computed<MenuItemWithIcon[]>(() => {
    this.translations();
    if (!this.measure()) return [];
    const block = this.deletionBlock();
    const keys = measureDeletionKeys(this.kind);
    const tooltip =
      block?.reason === 'referenced'
        ? this.translate.translate(keys.referenced, { names: block.names })
        : block?.reason === 'used'
          ? this.translate.translate(keys.used)
          : null;
    return [
      {
        label: this.translate.translate('common.buttons.delete'),
        materialIcon: 'delete',
        styleClass: 'p-menuitem-danger',
        disabled: block !== null,
        ...(tooltip !== null ? { title: tooltip, tooltipPosition: 'left' } : { command: () => void this.confirmDeleteMeasure() }),
      },
    ];
  });

  protected readonly kindLabelKey =
    this.kind === 'automatic'
      ? 'entities.measure.types.automatic'
      : this.kind === 'analysis-post'
        ? 'entities.measure.types.analysisPost'
        : 'entities.measure.types.manual';

  // Masqué tant que la gestion des appareils de mesure n'est pas livrée
  protected readonly showDeviceTab = false;

  protected readonly breadcrumbTrail: Signal<MenuItem[]> = computed(() => {
    const measure = this.measure();
    const items: MenuItem[] = [
      {
        label: this.translate.translate('navigation.measures'),
        routerLink: ['/assets', measure?.assetId, 'configuration', 'measures', this.kind],
      },
    ];

    if (measure?.name) {
      items.push({ label: measure.name });
    }

    return items;
  });

  constructor() {
    this.dataService.setKind(this.kind);
  }

  ngOnInit(): void {
    this.dataService.initRouteListener();

    const measureId = this.dataService.extractMeasureIdFromCurrentRoute();
    if (measureId) {
      void this.dataService.loadData(measureId);
    } else {
      this.dataService.stopDataLoading();
    }

    this.route.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const childPath = this.route.firstChild?.snapshot?.url[0]?.path;
      if (childPath && (this.tabs as readonly string[]).includes(childPath)) {
        this.activeTab = childPath;
      }
    });
  }

  private async confirmDeleteMeasure(): Promise<void> {
    const measure = this.measure();
    if (!measure) return;
    const detailLines = await this.deletionDetailLines(measure.id);
    this.confirmationHelper.confirmDelete(
      measure.name,
      measureDeletionKeys(this.kind).confirm,
      () => this.deleteMeasure(),
      detailLines,
    );
  }

  // Ce que la suppression emporte (valeurs calculées, retraits tableaux/graphiques),
  // affiché dans la confirmation. Purement informatif : une erreur de chargement
  // n'empêche pas de confirmer.
  private async deletionDetailLines(measureId: number): Promise<string[]> {
    if (this.kind !== 'analysis-post') return [];
    try {
      const impact = await this.dataService.loadDeletionImpact(measureId);
      return analysisPostDeletionDetailSentences(impact, (key, params) => this.translate.translate(key, params));
    } catch {
      return [];
    }
  }

  protected deleteMeasure(): void {
    const measure = this.measure();
    if (!measure) {
      console.error(this.translate.translate('common.errors.invalidMeasureDeletion'));
      return;
    }

    this.dataService
      .deleteMeasure()
      .then(() => {
        void this.router.navigate(['../..'], { relativeTo: this.route });
        this.toastService.success('entities.measure.messages.deleteSuccess', undefined, undefined, undefined, { name: measure.name });
      })
      .catch(error => {
        notifyMeasureDeletionError(error, this.toastService, this.kind);
        console.error(this.translate.translate('common.errors.measureDeleteFailed'), error);
      });
  }
}
